import { useCallback, useEffect, useRef, useState } from "react";
import { v4 } from "uuid";
import { placementContextHandler } from "../components/Realtime/PlacementContextHandler/ContextHandler.js";
import pennyBroadcast, { BroadcastType, usePennySubscriber } from "../utils/pennyBroadcast.js";
import { useRealTimeAudioPlayer } from "./useRealTimeAudioPlayer.js";
import { useRealTimeAudioRecorder } from "./useRealTimeAudioRecorder.js";
import { useRealTimeWebSocket } from "./useRealTimeWebSocket.js";
function useRealTimeChatCoordinator({ locale, realtimeContext, analytics, baseUrl, onLoadingChange, onOpenStateChange, pennysid, requestFrom, isCustomVoice = false }) {
    const [messages, setMessages] = useState([]);
    const [marker, setMarker] = useState(null);
    const [isActive, setIsActive] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const chatMessagesRef = useRef([]);
    const wsRef = useRef(null);
    const playAudioRef = useRef();
    const instanceRef = useRef(v4());
    const { playAudio, clearQueue } = useRealTimeAudioPlayer(analytics);
    useEffect(()=>{
        playAudioRef.current = playAudio;
    }, [
        playAudio
    ]);
    const handleMessage = useCallback((message)=>{
        switch(message.type){
            case 'session.created':
                placementContextHandler(wsRef, realtimeContext);
                break;
            case 'realtime_marker':
                setMarker(message.marker);
                break;
            case 'conversation.item.deleted':
                setMessages((prev)=>prev.filter((item)=>item.item_id !== message.item_id));
                break;
            case 'realtime_halt_playback':
                localStorage.setItem('isAudioInterrupted', 'yes');
                clearQueue();
                break;
            case 'response.audio.delta':
            case 'response.output_audio.delta':
                localStorage.setItem('isAudioInterrupted', 'no');
                playAudioRef.current?.(message.delta);
                break;
            case 'input_audio_buffer.speech_started':
                localStorage.setItem('isAudioInterrupted', 'yes');
                break;
            case "conversation.item.input_audio_transcription.completed":
                if (message.transcript) setMessages((prev)=>[
                        ...prev,
                        {
                            role: 'user',
                            content: message.transcript
                        }
                    ]);
                break;
            case "response.audio_transcript.done":
            case "response.output_audio_transcript.done":
                onLoadingChange?.(false);
                setMessages((prev)=>[
                        ...prev,
                        {
                            role: 'assistant',
                            content: message.transcript,
                            item_id: message.item_id
                        }
                    ]);
                break;
        }
    }, [
        onLoadingChange
    ]);
    const { isConnected, sendMessage } = useRealTimeWebSocket({
        url: baseUrl,
        locale,
        realtimeContext,
        onMessage: handleMessage,
        analytics,
        pennysid,
        requestFrom,
        isCustomVoice
    });
    const handleAudioChunk = useCallback((base64AudioData)=>{
        wsRef.current?.sendMessage({
            type: 'input_audio_buffer.append',
            audio: base64AudioData
        });
    }, []);
    const { isRecording, isMuted, mediaStream, startRecording, stopRecording, toggleMute } = useRealTimeAudioRecorder(handleAudioChunk, analytics);
    useEffect(()=>{
        wsRef.current = {
            sendMessage
        };
    }, [
        sendMessage
    ]);
    useEffect(()=>{
        chatMessagesRef.current = messages;
    }, [
        messages
    ]);
    const commit = useCallback(()=>{
        wsRef.current?.sendMessage({
            type: 'input_audio_buffer.commit'
        });
    }, []);
    const startChat = useCallback(async ()=>{
        if (isActive || !isConnected) return;
        try {
            await startRecording();
            setIsActive(true);
            onLoadingChange?.(true);
        } catch (error) {
            analytics.logError({
                loggedFrom: 'useRealTimeChatCoordinator.startChat',
                message: 'Failed to start chat',
                error
            });
        }
    }, [
        isActive,
        isConnected,
        startRecording,
        onLoadingChange,
        analytics
    ]);
    const stopChat = useCallback(()=>{
        if (!isActive) return;
        stopRecording();
        localStorage.setItem('isAudioInterrupted', 'yes');
        clearQueue();
        setIsActive(false);
        onLoadingChange?.(false);
    }, [
        isActive,
        stopRecording,
        clearQueue,
        commit,
        onLoadingChange
    ]);
    useEffect(()=>{
        if (isConnected && isInitializing) {
            startChat();
            setIsInitializing(false);
        }
    }, [
        isConnected,
        isInitializing,
        startChat
    ]);
    useEffect(()=>()=>{
            if (isActive) stopChat();
        }, [
        stopChat
    ]);
    const pennyBroadcastListener = useCallback((event)=>{
        if (event.data.type === BroadcastType.NEW_REALTIME_VOICE_INSTANCE && event.data.instanceId !== instanceRef.current) {
            setIsActive(false);
            clearQueue();
            stopChat();
            onOpenStateChange(false);
        }
    }, []);
    usePennySubscriber(pennyBroadcastListener);
    useEffect(()=>{
        pennyBroadcast({
            type: BroadcastType.NEW_REALTIME_VOICE_INSTANCE,
            instanceId: instanceRef.current,
            sender: 'useRealTimeChatCoordinator.useEffect'
        });
    }, []);
    return {
        isActive,
        isRecording,
        isConnected,
        isMuted,
        messages,
        marker,
        mediaStream,
        startChat,
        stopChat,
        toggleMute,
        setMarker
    };
}
export { useRealTimeChatCoordinator };
